class PredictiveSearch extends HTMLFormElement {
  /* Longest the drawer waits for the fallback suggested searches before painting without them. */
  static SUGGESTED_SEARCH_WAIT_MS = 600;

  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }

    /* Tagalys. Stays false when the SDK is disabled or has not loaded yet, in which
       case every path below falls back to native Shopify predictive search. */
    this.tagalysReady = false;
    this.connector = null;
    this.setupTagalys();
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }
  get resultsContainer() {
    return document.getElementById('PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this));
  }

  /* ---------------------------------------------------------------- Tagalys */

  setupTagalys() {
    if (typeof window.onTagalysReady !== 'function') return;

    window.onTagalysReady((Tagalys) => {
      /* One connector per page, shared by every <form is="predictive-search"> (header
         drawer + the /search landing field). Responses are broadcast and only the form
         that issued the query paints them. */
      if (!window.tagalysSearchSuggestions) {
        /* As-you-type text suggestions (popular / attribute searches) live in
           account-specific sections whose ids come from Tagalys, set in theme settings.
           An id the account does not define comes back under `undefined_sections` and
           is harmless. */
        const textSections = {};
        this.textSuggestionSectionIds().forEach((id) => {
          textSections[id] = { count: 5 };
        });
        window.tagalysSearchSuggestions = Tagalys.APIConnector.SearchSuggestions.new({
          params: {
            sections: {
              products: { include: ['total_count', 'items'], count: parseInt(this.dataset.resultsLimit, 10) || 4 },
              /* Link sections configured on the Eureka account (verified against the
                 API 2026-09-10): `collections` and `pages` return {title, link}
                 items; `include` is only accepted on `products`. */
              collections: { count: 5 },
              pages: { count: 5 },
              ...textSections,
            },
          },
          onSuccess: (response, helpers) => {
            document.dispatchEvent(new CustomEvent('tagalys:suggestions', { detail: { response, helpers } }));
          },
          onFailure: () => {
            document.dispatchEvent(new CustomEvent('tagalys:suggestions', { detail: { response: null } }));
          },
        });
      }
      this.connector = window.tagalysSearchSuggestions;
      this.tagalysReady = true;

      document.addEventListener('tagalys:suggestions', (event) => {
        if (window.tagalysActiveSuggestionForm !== this) return;
        this.renderTagalysSuggestions(event.detail.response);
      });
      this.addEventListener('submit', this.recordRecentSearch.bind(this));
      this.loadIdleSearches();
      this.loadIdleTrending(Tagalys);
    });
  }

  /* Idle state: trending products from a Tagalys smart widget (Theme settings > Tagalys
     search > Trending products smart widget ID). The merchant's picked products are
     already in the grid as the fallback; they are only replaced once the widget has
     returned products and its cards are built, so a slow or failed widget leaves the
     drawer exactly as it was. The widget is fetched once per page and shared by every
     predictive-search form. */
  loadIdleTrending(sdk) {
    const holder = this.querySelector('[data-tagalys-trending]');
    const grid = holder && holder.querySelector('[data-tagalys-trending-grid]');
    const widgetId = holder ? String(holder.dataset.tagalysTrendingId || '').trim() : '';
    const recommendations = sdk && sdk.APIConnector && sdk.APIConnector.Recommendations;
    if (!grid || !widgetId || !recommendations || typeof window.renderTagalysProductCards !== 'function') return;

    window.tagalysTrendingRequests = window.tagalysTrendingRequests || {};
    const count = parseInt(holder.dataset.count, 10) || 4;
    const key = `${widgetId}:${count}`;
    if (!window.tagalysTrendingRequests[key]) {
      window.tagalysTrendingRequests[key] = new Promise((resolve) => {
        try {
          recommendations.call({
            params: { recommendationId: widgetId, count },
            onSuccess: (response) => resolve((response && response.products) || []),
            onFailure: () => resolve([]),
          });
        } catch (error) {
          resolve([]);
        }
      });
    }

    window.tagalysTrendingRequests[key].then((products) => {
      if (!products.length) return;
      window
        .renderTagalysProductCards(products, {
          enableQuickAdd: false,
          showVendor: this.dataset.showVendor === 'true',
          imageSizes: '(max-width: 767px) 60vw, 265px',
          sectionId: FoxTheme.utils.getSectionId(this),
        })
        .then((cards) => {
          if (!cards.length || !grid.isConnected) return;
          grid.replaceChildren(...cards);
        })
        .catch(() => {
          /* Picked products stay as the fallback. */
        });
    });
  }

  /* Idle state: Tagalys recent + popular searches. The merchant's Popular Searches /
     Popular Pages links stay in place unless Tagalys has entries to show. */
  loadIdleSearches() {
    const helpers = this.connector && this.connector.helpers;
    if (!helpers || typeof helpers.getRecentAndPopularSearches !== 'function') return;

    Promise.resolve(helpers.getRecentAndPopularSearches(5, 10))
      .then((data) => {
        const recent = (data && data.recentSearches) || [];
        const popular = (data && data.popularSearches) || [];
        const searchUrl = (window.tagalysConfig && window.tagalysConfig.searchUrl) || '/search';

        const linkList = (entries) =>
          entries
            .map((entry) => {
              const query = typeof entry === 'string' ? entry : entry.query;
              if (!query) return '';
              const href = `${searchUrl}?q=${encodeURIComponent(query)}`;
              return `<li><a class="search__link reversed-link" href="${href}">${this.escape(query)}</a></li>`;
            })
            .join('');

        const popularGroup = this.querySelector('[data-tagalys-popular-group]');
        const popularList = this.querySelector('[data-tagalys-popular]');
        if (popular.length && popularList) {
          popularList.innerHTML = linkList(popular);
          if (popularGroup) popularGroup.hidden = false;
        }

        const recentGroup = this.querySelector('[data-tagalys-recent-group]');
        const recentList = this.querySelector('[data-tagalys-recent]');
        if (recent.length && recentGroup && recentList) {
          recentList.innerHTML = linkList(recent);
          recentGroup.hidden = false;
        }

        if ((popular.length && popularList) || (recent.length && recentList)) {
          this.searchContent?.classList.remove('hidden');
        }
      })
      .catch(() => {
        /* Idle links stay as the merchant configured them. */
      });
  }

  recordRecentSearch() {
    const term = this.getQuery();
    if (!term.length || !this.connector) return;
    try {
      const helpers = this.connector.helpers;
      helpers.recordRecentSearch(helpers.getEncodedQueryString({ query: term }));
    } catch (error) {
      /* Never block the submit on the recent-search write. */
    }
  }

  textSuggestionSectionIds() {
    const ids = (window.tagalysConfig && window.tagalysConfig.textSuggestionSections) || [];
    return ids.filter((id) => id && !['products', 'collections', 'pages'].includes(id));
  }

  /* Tagalys text suggestions: every returned section that is not products / collections
     / pages. Items carry `query` (search_queries) or `title` (links); a `link` or
     `queryString` keeps any filters the suggestion was built with. */
  tagalysTextSuggestions(sections) {
    const searchUrl = (window.tagalysConfig && window.tagalysConfig.searchUrl) || '/search';
    const entries = [];
    Object.keys(sections || {})
      .filter((key) => !['products', 'collections', 'pages'].includes(key))
      .forEach((key) => {
        ((sections[key] || {}).items || []).forEach((item) => {
          const text = item && (item.query || item.title);
          if (!text) return;
          let href = `${searchUrl}?q=${encodeURIComponent(text)}`;
          if (item.link) href = item.link;
          else if (item.queryString) href = `${searchUrl}?${item.queryString}`;
          entries.push({ text, href });
        });
      });
    return entries;
  }

  /* Fallback while the Tagalys account has no text-suggestion section: Shopify's own
     suggested searches, the list the native drawer showed in this slot. */
  fetchShopifySuggestedSearches(term) {
    this.suggestedSearchCache = this.suggestedSearchCache || new Map();
    if (this.suggestedSearchCache.has(term)) return this.suggestedSearchCache.get(term);

    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}.json`);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'query');
    url.searchParams.set('resources[limit]', 5);

    const request = fetch(url.toString())
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const queries = (data && data.resources && data.resources.results && data.resources.results.queries) || [];
        return queries.filter((query) => query && query.text).map((query) => ({ text: query.text, href: query.url }));
      })
      .catch(() => []);
    this.suggestedSearchCache.set(term, request);
    return request;
  }

  suggestedSearchesMarkup(entries) {
    const strings = (window.tagalysCardStrings && window.tagalysCardStrings.search) || {};
    return (
      '<div class="search__terms predictive-search-result predictive-search-result--queries">' +
      `<h4 class="search__col-title h5 predictive-search-result__heading">${this.escape(strings.suggestedSearches || '')}</h4>` +
      '<ul id="predictive-search-results-queries-list" class="search__link-list" role="group" aria-labelledby="predictive-search-queries">' +
      entries
        .map(
          (entry, index) =>
            `<li id="predictive-search-option-query-${index + 1}" class="predictive-search-result__list-item" role="option" aria-selected="false">` +
            `<a href="${this.escape(entry.href)}" class="search__link reversed-link">${this.escape(entry.text)}</a></li>`
        )
        .join('') +
      '</ul></div>'
    );
  }

  escape(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* Suggested searches resolve before anything is painted, so the left column and the
     product cards arrive together. Tagalys text sections win; while the account has none,
     Shopify's suggested searches (requested alongside the Tagalys query, so usually
     already back) fill the slot. A slow fallback never holds the drawer past
     SUGGESTED_SEARCH_WAIT_MS. */
  renderTagalysSuggestions(response) {
    const term = this.getQuery();
    /* A slow response must not resurrect results for a query since cleared, and a
       response for an earlier query must not overwrite a later one. */
    if (!term.length) return;
    if (response && response.query && String(response.query).trim() !== term) return;

    const sections = (response && response.sections) || {};
    const hasProducts = (((sections.products || {}).items) || []).length > 0;
    const tagalysText = this.tagalysTextSuggestions(sections);
    this.tagalysRenderId = (this.tagalysRenderId || 0) + 1;
    const renderId = this.tagalysRenderId;

    let suggestedSearches;
    if (tagalysText.length || !hasProducts) {
      suggestedSearches = Promise.resolve(tagalysText);
    } else {
      suggestedSearches = Promise.race([
        this.fetchShopifySuggestedSearches(term),
        new Promise((resolve) => setTimeout(() => resolve([]), PredictiveSearch.SUGGESTED_SEARCH_WAIT_MS)),
      ]);
    }

    suggestedSearches.then((textSuggestions) => {
      /* Superseded by a newer response, or the query moved on while waiting. */
      if (this.tagalysRenderId !== renderId || this.getQuery() !== term) return;
      this.paintTagalysSuggestions(response, term, textSuggestions);
    });
  }

  paintTagalysSuggestions(response, term, textSuggestions) {
    const target = this.resultsContainer;
    if (!target) return;

    const strings = (window.tagalysCardStrings && window.tagalysCardStrings.search) || {};
    const helpersJs = window.tagalysCardHelpers || {};
    const interpolate = helpersJs.interpolate || ((tpl, values) => String(tpl || '').replace('[terms]', values.terms));
    const sections = (response && response.sections) || {};
    const products = sections.products || {};
    const items = products.items || [];
    const linkGroups = [
      { key: 'collections', heading: strings.collections, kind: 'collection' },
      { key: 'pages', heading: strings.suggestedPages, kind: 'article-page' },
    ]
      .map((group) => ({ ...group, links: ((sections[group.key] || {}).items || []).filter((item) => item && item.title && item.link) }))
      .filter((group) => group.links.length);

    this.removeAttribute('loading');
    this.resetButton.classList.remove('btn--loading');
    this.searchContent?.classList.remove('hidden');
    this.setAttribute('results', 'true');

    if (!items.length) {
      target.innerHTML =
        '<div class="search__no-results"><div class="search__no-results-text">' +
        `<h3 class="search__no-results-heading">${interpolate(strings.noResultsText, { terms: this.escape(term) })}</h3>` +
        `<p class="search__no-results-body m-0">${this.escape(strings.noResultsMessage)}</p>` +
        '</div></div>';
      return;
    }

    /* Left column mirrors the native predictive-search markup (suggested searches,
       collections, then articles/pages) so the drawer stylesheet applies unchanged. */
    const leftColumn = linkGroups.length || textSuggestions.length
      ? '<div class="search__drawer-col search__drawer-col--left">' +
        (textSuggestions.length ? this.suggestedSearchesMarkup(textSuggestions) : '') +
        linkGroups
          .map(
            (group) =>
              `<div class="search__terms predictive-search-result predictive-search-result--${group.key}">` +
              `<h4 class="search__col-title h5 predictive-search-result__heading">${this.escape(group.heading || '')}</h4>` +
              `<ul id="predictive-search-results-${group.kind}-list" class="search__link-list" role="group" aria-labelledby="predictive-search-${group.kind}">` +
              group.links
                .map(
                  (item, index) =>
                    `<li id="predictive-search-option-${group.kind}-${index + 1}" role="option" aria-selected="false">` +
                    `<a href="${this.escape(item.link)}" class="reversed-link">${this.escape(item.title)}</a></li>`
                )
                .join('') +
              '</ul></div>'
          )
          .join('') +
        '</div>' +
        '<div class="search__drawer-divider" aria-hidden="true"></div>'
      : '';

    target.innerHTML =
      '<div class="search__drawer-main">' +
      leftColumn +
      '<div class="search__drawer-col search__drawer-col--right predictive-search-result predictive-search-result--products">' +
      '<div class="search__carousel-header">' +
      `<h4 class="search__col-title h5 predictive-search-result__heading">${this.escape(strings.suggestedProducts)}</h4>` +
      `<button type="submit" class="search__results-all reversed-link font-body-bolder">${interpolate(strings.searchFor, { terms: this.escape(term) })}</button>` +
      '</div>' +
      '<div id="predictive-search-results-products-list" class="search__product-grid" role="group" aria-labelledby="predictive-search-products"></div>' +
      '</div></div>';

    const grid = target.querySelector('#predictive-search-results-products-list');
    if (typeof window.renderTagalysProductCards !== 'function') return;

    window
      .renderTagalysProductCards(items, {
        enableQuickAdd: false,
        showVendor: this.dataset.showVendor === 'true',
        imageSizes: '(max-width: 767px) 60vw, 265px',
        sectionId: FoxTheme.utils.getSectionId(this),
      })
      .then((cards) => {
        /* The query may have moved on while the cards were being built. */
        if (this.getQuery() !== term || !grid.isConnected) return;
        cards.forEach((card, index) => {
          const cell = document.createElement('div');
          cell.id = `predictive-search-option-product-${index + 1}`;
          cell.setAttribute('role', 'option');
          cell.setAttribute('aria-selected', 'false');
          cell.appendChild(card);
          grid.appendChild(cell);
        });
      });
  }

  /* ----------------------------------------------------------------- native */

  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      if (this.tagalysReady) {
        this.requestTagalys();
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }
  }
  getQuery() {
    return this.input.value.trim();
  }
  clear(event = null) {
    event && event.preventDefault();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.removeAttribute('loading');
    this.resetButton.classList.remove('btn--loading');
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    return (
      url.searchParams.set('q', this.getQuery()),
      // Figma 3822:48480: the results panel pairs Suggested Searches / Pages with
      // the product cards — request every resource type, not the product-only default.
      url.searchParams.set('resources[type]', 'product,query,collection,page,article'),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      // Predictive search omits SKU/barcode from its default field set, so a
      // typed SKU (e.g. TABMONTG240) found nothing here but matched on the full
      // /search page. Name every searchable field so SKU lookups work too.
      url.searchParams.set(
        'resources[options][fields]',
        'title,product_type,variants.title,vendor,variants.sku,variants.barcode,tag,author,body'
      ),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    if (this.tagalysReady) {
      this.requestTagalys();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  requestTagalys() {
    window.tagalysActiveSuggestionForm = this;
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');
    try {
      /* Started with the Tagalys query so the fallback suggested searches are ready when
         the response lands (cached per term). */
      this.fetchShopifySuggestedSearches(this.getQuery());
      this.connector.helpers.updateQuery(this.getQuery());
    } catch (error) {
      this.removeAttribute('loading');
      this.resetButton.classList.remove('btn--loading');
    }
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    // Indicate that loading has started
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((responseText) => {
        // Process and cache the fetched data
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        // Remove the loading attribute in both success and error cases
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
        this.setAttribute('results', 'true');
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);
